"use client";

import React, { useRef, useState } from "react";
import Icon from "@/components/common/Icon";
import LibraryBadges from "@/components/common/LibraryBadges";
import { useLocale, useLocalizedData } from "@/context/LocaleContext";
import { useSearch } from "@/context/SearchContext";
import { Branding } from "@/types/branding";
import { I18nData } from "@/types/i18n";
import { Journal } from "@/types/journal";
import {
  generatePaymentLink,
  generateLicenseLink,
  generatePublishingModelLink,
  generateJournalLink,
} from "@/utils/links";
import MobileCollapsibleSection from "./mobile-collapsible-row";
import Linkify from "linkify-react";
import { AgreementModal } from "./agreement-modal";
import { trackUserEvent } from "@/app/api/client-only";
import { QualityScoreEntry } from "./quality-score-entry";

interface Props {
  journal: Journal;
  i18nData: I18nData;
  branding: Branding | null;
}

export default function ResultCard({ journal, i18nData, branding }: Props) {
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const paymentButtonRef = useRef<HTMLButtonElement>(null);
  const { locale } = useLocale();
  const localizedData = useLocalizedData(i18nData);
  const qualitySystemsList = branding?.extensions?.quality_lists;

  const { slug } = useSearch();
  const showAgreementModal =
    branding?.show_agreement_description && journal.agreement_id;
  const paymentIcon = journal.payment
    ? localizedData?.payments?.[journal.payment]
    : undefined;
  const licenseIcons = journal.licenses?.map(
    (license) => localizedData.licenses?.[license],
  );

  const publishingModelIcon = journal.publishing_model
    ? localizedData?.publishing_models?.[journal.publishing_model]
    : undefined;

  const linkTarget = branding?.open_in_new_window ? "_blank" : "_self";
  const paymentLabel =
    paymentIcon?.name || i18nData[locale]?.payment_title || "Payment";

  // Opening the pop-up is recorded as agreement_description. The payment link
  // inside it is recorded as payments.
  const openAgreementModal = () => {
    setIsAgreementOpen(true);
    trackUserEvent(slug, "d", "payment", {
      journal: journal._id,
      agreement: journal.agreement_id,
    }).catch(() => {});
  };

  const PaymentLinkElement = ({ children }: { children: React.ReactNode }) => {
    if (showAgreementModal) {
      return (
        <button
          ref={paymentButtonRef}
          type="button"
          aria-haspopup="dialog"
          aria-label={paymentLabel}
          className="paymentModalButton accessibleLink clickable-icon-cell"
          onClick={openAgreementModal}
        >
          {children}
        </button>
      );
    }
    if (journal.payment_url) {
      const paymentUrl = journal._id
        ? generatePaymentLink(journal._id, slug)
        : "#";
      return (
        <a
          href={paymentUrl}
          target={paymentUrl !== "#" ? linkTarget : undefined}
          aria-label={paymentLabel}
          className="accessibleLink clickable-icon-cell"
        >
          {children}
        </a>
      );
    } else {
      return (
        <div
          title={localizedData?.journal_link_tooltip}
          className="tooltip inline-block"
        >
          {children}
        </div>
      );
    }
  };

  const JournalElement = ({ children }: { children: React.ReactNode }) => {
    if (journal.url) {
      return (
        <a
          id="cardName"
          href={journal._id ? generateJournalLink(journal._id, slug) : "#"}
          target={journal._id ? linkTarget : undefined}
          className="accessibleLink"
        >
          {children}
        </a>
      );
    } else {
      return (
        <div
          title={localizedData?.journal_link_tooltip}
          className="tooltip inline-block"
        >
          {children}
        </div>
      );
    }
  };

  return (
    <div id="resultCard">
      <JournalElement>{journal.name}</JournalElement>
      <LibraryBadges
        branding={branding}
        journalCollections={journal.collections}
        collectionsMap={branding?.collections}
        i18nData={i18nData}
      />
      <div id="cardImprint">{journal.imprint}</div>
      <div id="iconWrapper"></div>
      <div className="mt-[10px]">
        {paymentIcon && (
          <PaymentLinkElement>
            <Icon
              icon={paymentIcon}
              width={44}
              height={44}
              showArrow={Boolean(showAgreementModal || journal.payment_url)}
              alt={
                paymentIcon?.name ||
                i18nData[locale]?.payment_title ||
                "Payment"
              }
            />
          </PaymentLinkElement>
        )}
        <p className="mobileCardSubTooltip">
          {paymentIcon?.description?.trim()}
        </p>
      </div>
      {qualitySystemsList && qualitySystemsList.length > 0 && (
        <div className="mt-[10px]">
          <p className="mobileCardTooltip">
            {localizedData?.quality_title || "Quality"}
          </p>
          <div className="flex flex-col text-[14px]">
            {qualitySystemsList.map((system) => (
              <QualityScoreEntry
                key={system}
                system={system}
                score={journal.quality_scores?.[system]}
                localizedData={localizedData}
                linkTarget={linkTarget}
                orgSlug={slug}
              />
            ))}
          </div>
        </div>
      )}
      {licenseIcons &&
        licenseIcons.length > 0 &&
        licenseIcons.map((license, index) =>
          license ? (
            <div key={license.description || index} className="mt-[10px]">
              {(() => {
                // Only use universal link pattern, fallback to '#' if data is missing
                const licenseKey = journal.licenses?.[index];
                const finalUrl =
                  licenseKey && locale
                    ? generateLicenseLink(licenseKey, locale, slug)
                    : "#";

                const licenseLabel =
                  license?.name ||
                  i18nData[locale]?.license_options ||
                  "License";
                return (
                  <a
                    href={finalUrl}
                    target={finalUrl !== "#" ? linkTarget : undefined}
                    aria-label={licenseLabel}
                    className={`accessibleLink ${
                      finalUrl !== "#" ? "clickable-icon-cell" : ""
                    }`}
                  >
                    <Icon
                      icon={license}
                      width={94}
                      height={94}
                      showArrow={finalUrl !== "#"}
                      alt={
                        license?.name ||
                        i18nData[locale]?.license_options ||
                        "License"
                      }
                    />
                  </a>
                );
              })()}
              <p className="mobileCardSubTooltip">
                {license.description?.trim()}
              </p>
            </div>
          ) : null,
        )}
      <div className="mt-[10px]">
        {publishingModelIcon &&
          (() => {
            const hasPublishingModelLink =
              publishingModelIcon.url?.trim() ||
              branding?.publishing_model_url?.trim();

            const finalUrl =
              hasPublishingModelLink && journal.publishing_model && locale
                ? generatePublishingModelLink(
                    journal.publishing_model,
                    locale,
                    slug,
                  )
                : "";

            if (!finalUrl) {
              return (
                <div className="accessibleLink">
                  <Icon
                    icon={publishingModelIcon}
                    width={32}
                    height={32}
                    alt={
                      publishingModelIcon?.name ||
                      localizedData.publishing_model ||
                      "Publishing model"
                    }
                  />
                </div>
              );
            }

            return (
              <a
                href={finalUrl}
                target={linkTarget}
                rel={
                  linkTarget === "_blank" ? "noopener noreferrer" : undefined
                }
                aria-label={
                  publishingModelIcon?.name ||
                  localizedData.publishing_model ||
                  "Publishing model"
                }
                className="accessibleLink clickable-icon-cell"
              >
                <Icon
                  icon={publishingModelIcon}
                  width={24}
                  height={24}
                  showArrow
                  alt={
                    publishingModelIcon?.name ||
                    localizedData.publishing_model ||
                    "Publishing model"
                  }
                />
              </a>
            );
          })()}
        <p className="mobileCardTooltip">
          {publishingModelIcon?.name || journal.publishing_model}
        </p>
        <p className="mobileCardSubTooltip mt-[10px]">
          {publishingModelIcon?.description?.trim()}
        </p>
      </div>
      {journal.extra_info && journal.extra_info.trim().length > 0 && (
        <MobileCollapsibleSection
          collapsedTitle={localizedData.show_notes || "Show notes"}
          expandedTitle={localizedData.hide_notes || "Hide notes"}
        >
          <p>
            <Linkify
              options={{
                target: branding?.open_in_new_window ? "_blank" : "_self",
                rel: branding?.open_in_new_window
                  ? "noopener noreferrer"
                  : undefined,
              }}
            >
              {journal.extra_info}
            </Linkify>
          </p>
        </MobileCollapsibleSection>
      )}
      {showAgreementModal && isAgreementOpen && (
        <AgreementModal
          open={isAgreementOpen}
          onClose={() => setIsAgreementOpen(false)}
          journalName={journal.name}
          journalUrl={
            journal._id ? generateJournalLink(journal._id, slug) : undefined
          }
          journalId={journal._id}
          slug={slug}
          agreementId={journal.agreement_id!}
          localizedData={localizedData}
          linkTarget={linkTarget}
          returnFocusRef={paymentButtonRef}
        />
      )}
    </div>
  );
}
